import { Plus, X } from "lucide-react";
import CustomSelect from "../../../shared/CustomSelect";
import CustomInput from "../../../shared/CustomInput";
import Loading from "../../../shared/Loading";
import {
  addChattelImage,
  deleteChattelImage,
} from "../../../sdk/borrower/borrower";
import { uploadMultiImages } from "../../../sdk/upload/upload";
import { useRef, useState } from "react";
import { useError } from "../../../context/ErrorProvider";
import { useMutation } from "react-query";

const EditGuarantorChattel = ({
  closeEditGuarantorChattel,
  loading,
  guarantorChattelEditDetails,
  setGuarantorChattelEditDetails,
  chattel,
  handleEditGuarantorChattel,
  refetchLoanApplication,
  toggleEditGuarantorChattelSuccess,
}) => {
  const [urls, setUrls] = useState([]);
  const fileInputRef = useRef(null);
  const { showError } = useError();

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      const uploadedUrls = await uploadMultiImages(files);
      setUrls(uploadedUrls);
    } catch (err) {
      console.error("Failed to upload files:", err);
    }
  };

  const handleChooseFileClick = () => {
    fileInputRef.current.click();
  };

  const handleChange = (field, value) => {
    setGuarantorChattelEditDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const { mutate: addMyChattelImage } = useMutation({
    mutationKey: ["add guarantor chattel image"],
    mutationFn: () => addChattelImage(chattel?.id, urls),
    onSuccess: () => {
      refetchLoanApplication();
      closeEditGuarantorChattel();
      toggleEditGuarantorChattelSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const { mutate: deleteMyChattelImage } = useMutation({
    mutationKey: ["delete guarantor chattel image"],
    mutationFn: (id) => deleteChattelImage(chattel?.id, id),
    onSuccess: () => {
      refetchLoanApplication();
      closeEditGuarantorChattel();
      toggleEditGuarantorChattelSuccess();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleDeleteChattelImage = (id) => {
    deleteMyChattelImage(id);
  };

  const onNext = () => {
    if (urls.length > 0) {
      addMyChattelImage();
    }
    handleEditGuarantorChattel();
  };
  return (
    <div
      onClick={() => closeEditGuarantorChattel()}
      className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[880px] h-[600px] rounded-[10px] p-[20px] relative bg-white overflow-y-auto"
      >
        <div
          onClick={closeEditGuarantorChattel}
          className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#D63B3B] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
        >
          <X color="#ffffff" />
        </div>
        <p className="font-semibold text-[#000000] text-[16px]">
          Edit Guarantor Chattel
        </p>
        <p className="text-[15px] font-medium text-[#5C5C5C] mb-[20px]">
          Please update the guarantor chattel below to ensure accuracy of your
          loan application. Providing complete and correct information helps us
          verify your guarantor and process your request smoothly. All fields
          are required unless marked optional.
        </p>
        {/* Form */}
        <div className="flex flex-col gap-[10px]">
          <div className="flex items-start justify-between">
            <div className="w-[49%] flex flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Chattel Name
              </label>
              <CustomInput
                placeholder="Enter chattel name"
                type="text"
                value={guarantorChattelEditDetails.name}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>
            <div className="w-[49%] flex flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Location
              </label>
              <CustomInput
                placeholder="Enter chattel location"
                type="text"
                value={guarantorChattelEditDetails.location}
                onChange={(e) => handleChange("location", e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="w-[49%] flex flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Chattel Description
              </label>
              <CustomInput
                placeholder="Enter chattel description"
                type="text"
                value={guarantorChattelEditDetails.description}
                onChange={(e) => handleChange("description", e.target.value)}
              />
            </div>
            <div className="w-[49%] flex flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Chattel Value
              </label>
              <CustomInput
                placeholder="Enter chattel value"
                type="number"
                value={guarantorChattelEditDetails.value}
                onChange={(e) => handleChange("value", e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div className="w-[49%] flex flex-col">
              <label className="text-[#222222] font-medium text-[13px]">
                Condition
              </label>
              <CustomSelect
                value={guarantorChattelEditDetails.condition}
                onChange={(val) => handleChange("condition", val)}
                options={["New", "Old", "Good"]}
              />
            </div>
          </div>
          <p className="text-[#222222] my-[10px] text-[14px] font-normal">
            Chattel Images
          </p>
          <div className="w-full h-[130px] flex items-center gap-[10px]">
            {chattel?.images?.length > 0 ? (
              chattel.images.map((item, index) => (
                <div
                  key={index}
                  className="h-full w-[180px] rounded-[15px] relative overflow-hidden"
                >
                  <img
                    className="h-full w-full rounded-[15px] object-cover"
                    src={item?.imageUrl}
                    alt={`businessImage-${index}`}
                  />
                  <div
                    className="absolute bottom-0 left-0 w-full bg-black bg-opacity-40 text-[#000000] underline text-center py-2 text-sm cursor-pointer hover:bg-opacity-60"
                    onClick={() => handleDeleteChattelImage(item?.id)}
                  >
                    Delete
                  </div>
                </div>
              ))
            ) : (
              <div
                onClick={handleChooseFileClick}
                className="h-full bg-[#F3F3F3] w-[180px] cursor-pointer rounded-[15px] flex flex-col items-center justify-center"
              >
                <Plus size={24} color="#000" />
                <p className="text-center text-[12px] text-[#000]">
                  Add a photo Drag and Drop files here or{" "}
                  <span className="text-[#000000] underline">
                    Choose file here
                  </span>
                </p>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}

            {urls?.length > 0 &&
              urls?.map((url, index) => (
                <div
                  key={index}
                  className="h-full w-[180px] rounded-[15px] relative overflow-hidden"
                >
                  <img
                    className="h-full w-full rounded-[15px] object-cover"
                    src={url}
                    alt={`businessImage-${index}`}
                  />
                </div>
              ))}
            {chattel?.images?.length > 0 && (
              <div
                onClick={handleChooseFileClick}
                className="h-full bg-[#F3F3F3] w-[180px] cursor-pointer rounded-[15px] flex flex-col items-center justify-center"
              >
                <Plus size={24} color="#000" />
                <p className="text-center text-[12px] text-[#000]">
                  Add another photo Drag and Drop files here or{" "}
                  <span className="text-[#000000] underline">
                    Choose file here
                  </span>
                </p>
                <input
                  type="file"
                  multiple
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>
            )}
          </div>
        </div>
        <div className="absolute bottom-[20px] right-[20px] flex items-center justify-center gap-[10px]">
          <button
            onClick={closeEditGuarantorChattel}
            className="h-[35px] bg-[#F3F3F3] rounded-[30px] px-[15px] text-[#5C5C5C] font-medium"
          >
            Close
          </button>
          <button
            onClick={onNext}
            disabled={loading}
            className="h-[35px] bg-[#000000] flex items-center justify-center gap-[5px] rounded-[30px] px-[15px] text-[#FFFFFF] font-medium"
          >
            {loading && <Loading />}
            Edit and Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditGuarantorChattel;
